import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { isNonEmptyArray } from 'vscode/vscode/vs/base/common/arrays';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { DisposableStore, DisposableMap, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IProductService } from 'vscode/vscode/vs/platform/product/common/productService.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { ViewPaneContainer } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPaneContainer';
import { Extensions } from 'vscode/vscode/vs/workbench/common/views';
import { CHAT_VIEW_ID } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat';
import { ChatViewPane, CHAT_SIDEBAR_PANEL_ID } from './chatViewPane.js';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { IChatAgentService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents.service';
import { isProposedApiEnabled } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions';
import { ExtensionsRegistry } from 'vscode/vscode/vs/workbench/services/extensions/common/extensionsRegistry';

const _moduleId = "vs/workbench/contrib/chat/browser/chatParticipantContributions";
const chatParticipantExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'chatParticipants',
    jsonSchema: {
        description: ( localizeWithPath(_moduleId, 0, 'Contributes a chat participant')),
        type: 'array',
        items: {
            additionalProperties: false,
            type: 'object',
            defaultSnippets: [{ body: { name: '', description: '' } }],
            required: ['name', 'id'],
            properties: {
                id: {
                    description: ( localizeWithPath(_moduleId, 1, "A unique id for this chat participant.")),
                    type: 'string'
                },
                name: {
                    description: ( localizeWithPath(
                        _moduleId,
                        2,
                        "User-facing display name for this chat participant. The user will use '@' with this name to invoke the participant."
                    )),
                    type: 'string'
                },
                description: {
                    description: ( localizeWithPath(_moduleId, 3, "A description of this chat participant, shown in the UI.")),
                    type: 'string'
                },
                isDefault: {
                    markdownDescription: ( localizeWithPath(
                        _moduleId,
                        4,
                        "**Only** allowed for extensions that have the `defaultChatParticipant` proposal."
                    )),
                    type: 'boolean',
                },
                isSticky: {
                    description: ( localizeWithPath(
                        _moduleId,
                        5,
                        "Whether invoking the command puts the chat into a persistent mode, where the command is automatically added to the chat input for the next message."
                    )),
                    type: 'boolean'
                },
                defaultImplicitVariables: {
                    markdownDescription: '**Only** allowed for extensions that have the `chatParticipantAdditions` proposal. The names of the variables that are invoked by default',
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                },
                commands: {
                    markdownDescription: ( localizeWithPath(
                        _moduleId,
                        6,
                        "Commands available for this chat participant, which the user can invoke with a `/`."
                    )),
                    type: 'array',
                    items: {
                        additionalProperties: false,
                        type: 'object',
                        defaultSnippets: [{ body: { name: '', description: '' } }],
                        required: ['name'],
                        properties: {
                            name: {
                                description: ( localizeWithPath(
                                    _moduleId,
                                    7,
                                    "A short name by which this command is referred to in the UI, e.g. `fix` or * `explain` for commands that fix an issue or explain code. The name should be unique among the commands provided by this participant."
                                )),
                                type: 'string'
                            },
                            description: {
                                description: ( localizeWithPath(_moduleId, 8, "A description of this command.")),
                                type: 'string'
                            },
                            when: {
                                description: ( localizeWithPath(_moduleId, 9, "A condition which must be true to enable this command.")),
                                type: 'string'
                            },
                            sampleRequest: {
                                description: ( localizeWithPath(
                                    _moduleId,
                                    10,
                                    "When the user clicks this command in `/help`, this text will be submitted to this participant."
                                )),
                                type: 'string'
                            },
                            isSticky: {
                                description: ( localizeWithPath(
                                    _moduleId,
                                    5,
                                    "Whether invoking the command puts the chat into a persistent mode, where the command is automatically added to the chat input for the next message."
                                )),
                                type: 'boolean'
                            },
                            defaultImplicitVariables: {
                                markdownDescription: ( localizeWithPath(
                                    _moduleId,
                                    11,
                                    "**Only** allowed for extensions that have the `chatParticipantAdditions` proposal. The names of the variables that are invoked by default"
                                )),
                                type: 'array',
                                items: {
                                    type: 'string'
                                }
                            },
                        }
                    }
                },
                locations: {
                    markdownDescription: ( localizeWithPath(_moduleId, 12, "Locations in which this chat participant is available.")),
                    type: 'array',
                    default: ['panel'],
                    items: {
                        type: 'string',
                        enum: ['panel', 'terminal', 'notebook']
                    }
                }
            }
        }
    },
    activationEventsGenerator: (contributions, result) => {
        for (const contrib of contributions) {
            result.push(`onChatParticipant:${contrib.id}`);
        }
    },
});
let ChatExtensionPointHandler = class ChatExtensionPointHandler {
    static { this.ID = 'workbench.contrib.chatExtensionPointHandler'; }
    constructor(_chatAgentService, productService, contextService, logService) {
        this._chatAgentService = _chatAgentService;
        this.productService = productService;
        this.contextService = contextService;
        this.logService = logService;
        this.disposables = ( (new DisposableStore()));
        this._participantRegistrationDisposables = ( (new DisposableMap()));
        this._viewContainer = this.registerViewContainer();
        this.registerListeners();
        this.handleAndRegisterChatExtensions();
    }
    registerListeners() {
        this.contextService.onDidChangeContext(e => {
            if (!this.productService.chatWelcomeView) {
                return;
            }
            const showWelcomeViewConfigKey = 'workbench.chat.experimental.showWelcomeView';
            const keys = ( (new Set([showWelcomeViewConfigKey])));
            if (e.affectsSome(keys)) {
                const contextKeyExpr = ( (ContextKeyExpr.equals(showWelcomeViewConfigKey, true)));
                const viewsRegistry = ( (Registry.as(Extensions.ViewsRegistry)));
                if (this.contextService.contextMatchesRules(contextKeyExpr)) {
                    this._welcomeViewDescriptor = {
                        id: CHAT_VIEW_ID,
                        name: { original: this.productService.chatWelcomeView.welcomeViewTitle, value: this.productService.chatWelcomeView.welcomeViewTitle },
                        containerIcon: this._viewContainer.icon,
                        ctorDescriptor: ( (new SyncDescriptor(ChatViewPane))),
                        canToggleVisibility: false,
                        canMoveView: true,
                        order: 100
                    };
                    viewsRegistry.registerViews([this._welcomeViewDescriptor], this._viewContainer);
                    viewsRegistry.registerViewWelcomeContent(CHAT_VIEW_ID, {
                        content: this.productService.chatWelcomeView.welcomeViewContent,
                    });
                }
                else if (this._welcomeViewDescriptor) {
                    viewsRegistry.deregisterViews([this._welcomeViewDescriptor], this._viewContainer);
                }
            }
        }, null, this.disposables);
    }
    handleAndRegisterChatExtensions() {
        chatParticipantExtensionPoint.setHandler((extensions, delta) => {
            for (const extension of delta.added) {
                for (const providerDescriptor of extension.value) {
                    if (providerDescriptor.isDefault && !isProposedApiEnabled(extension.description, 'defaultChatParticipant')) {
                        this.logService.error(`Extension '${extension.description.identifier.value}' CANNOT use API proposal: defaultChatParticipant.`);
                        continue;
                    }
                    if (providerDescriptor.defaultImplicitVariables && !isProposedApiEnabled(extension.description, 'chatParticipantAdditions')) {
                        this.logService.error(`Extension '${extension.description.identifier.value}' CANNOT use API proposal: chatParticipantAdditions.`);
                        continue;
                    }
                    if (!providerDescriptor.id || !providerDescriptor.name) {
                        this.logService.error(`Extension '${extension.description.identifier.value}' CANNOT register participant without both id and name.`);
                        continue;
                    }
                    const store = ( (new DisposableStore()));
                    if (providerDescriptor.isDefault && (!providerDescriptor.locations || providerDescriptor.locations?.includes(ChatAgentLocation.Panel))) {
                        store.add(this.registerDefaultParticipantView(providerDescriptor));
                    }
                    store.add(this._chatAgentService.registerAgent(providerDescriptor.id, {
                        extensionId: extension.description.identifier,
                        extensionPublisherDisplayName: extension.description.publisherDisplayName ?? extension.description.publisher,
                        extensionPublisherId: extension.description.publisher,
                        extensionDisplayName: extension.description.displayName ?? extension.description.name,
                        id: providerDescriptor.id,
                        description: providerDescriptor.description,
                        metadata: {
                            isSticky: providerDescriptor.isSticky,
                        },
                        name: providerDescriptor.name,
                        isDefault: providerDescriptor.isDefault,
                        defaultImplicitVariables: providerDescriptor.defaultImplicitVariables,
                        locations: isNonEmptyArray(providerDescriptor.locations) ?
                            ( (providerDescriptor.locations.map(ChatAgentLocation.fromRaw))) :
                            [ChatAgentLocation.Panel],
                        slashCommands: providerDescriptor.commands ?? []
                    }));
                    this._participantRegistrationDisposables.set(getParticipantKey(extension.description.identifier, providerDescriptor.id), store);
                }
            }
            for (const extension of delta.removed) {
                for (const providerDescriptor of extension.value) {
                    this._participantRegistrationDisposables.deleteAndDispose(getParticipantKey(extension.description.identifier, providerDescriptor.name));
                }
            }
        });
    }
    registerViewContainer() {
        const title = ( localize2WithPath(_moduleId, 13, "Chat"));
        const icon = Codicon.commentDiscussion;
        const viewContainerId = CHAT_SIDEBAR_PANEL_ID;
        const viewContainer = ( (Registry.as(Extensions.ViewContainersRegistry))).registerViewContainer({
            id: viewContainerId,
            title,
            icon,
            ctorDescriptor: ( (new SyncDescriptor(
                ViewPaneContainer,
                [viewContainerId, { mergeViewWithContainerWhenSingleView: true }]
            ))),
            storageId: viewContainerId,
            hideIfEmpty: true,
            order: 100,
        }, 0 );
        return viewContainer;
    }
    registerDefaultParticipantView(defaultParticipantDescriptor) {
        const viewDescriptor = [{
                id: CHAT_VIEW_ID,
                containerIcon: this._viewContainer.icon,
                containerTitle: this._viewContainer.title.value,
                singleViewPaneContainerTitle: this._viewContainer.title.value,
                name: { value: defaultParticipantDescriptor.name, original: defaultParticipantDescriptor.name },
                canToggleVisibility: false,
                canMoveView: true,
                ctorDescriptor: ( (new SyncDescriptor(ChatViewPane))),
            }];
        ( (Registry.as(Extensions.ViewsRegistry))).registerViews(viewDescriptor, this._viewContainer);
        return toDisposable(() => {
            ( (Registry.as(Extensions.ViewsRegistry))).deregisterViews(viewDescriptor, this._viewContainer);
        });
    }
};
ChatExtensionPointHandler = ( (__decorate([
    ( (__param(0, IChatAgentService))),
    ( (__param(1, IProductService))),
    ( (__param(2, IContextKeyService))),
    ( (__param(3, ILogService)))
], ChatExtensionPointHandler)));
function getParticipantKey(extensionId, participantName) {
    return `${extensionId.value}_${participantName}`;
}

export { ChatExtensionPointHandler };
